import logger from '../utils/logger';

const jsonFetch = async (url, options = {}) => {
  const method = options.method || 'GET';
  logger.apiRequest(method, url, options.body ? JSON.parse(options.body) : null);
  
  try {
    const response = await fetch(url, options);
    const data = await response.json().catch(() => ({}));
    
    if (!response.ok) {
      // `data.error` faz parte da cadeia: os endpoints de reembolso respondem
      // com {error, code} e sem isto *qualquer* falha aparecia como
      // "Request failed", que não diz nada a quem está ao telefone com o cliente.
      const error = new Error(data.error || data.message || data.msg || 'Request failed');
      error.code = data.code;
      error.status = response.status;
      error.data = data;
      logger.apiError(method, url, error);
      throw error;
    }
    
    logger.apiResponse(method, url, response.status, data);
    return data;
  } catch (error) {
    if (!error.status) {
      // Network error
      logger.apiError(method, url, { message: error.message, status: 0 });
    }
    throw error;
  }
};

const authHeaders = (token) => ({
  'Content-Type': 'application/json',
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

export const login = async (baseUrl, credentials) => {
  return jsonFetch(`${baseUrl}/toms/login`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(credentials),
  });
};

// Encomendas do painel.
//
// `params` aceita `restaurante`, `tipo`, `refund_request`, `offset` e ainda:
//
//   status  um estado, ou vários separados por vírgulas. O histórico precisa
//           de `completed,cancelled,refunded` e três chamadas para o mesmo
//           ecrã eram três vezes o custo.
//   from/to YYYY-MM-DD, inclusivos, sobre a hora da última mudança de estado.
//           Sem eles a única forma de ver mais atrás era aumentar o `limit` e
//           arrastar tudo o que estava pelo meio.
//   limit   0 desliga o limite. É o "Todos" do histórico, e não tem outra
//           forma de ser expresso — qualquer teto acabaria por cortar o
//           histórico de alguém sem o dizer.
//
// `signal` vem de um AbortController: o "Todos" pode trazer a tabela inteira, e
// quem o pediu tem de o poder desistir sem ficar com a página presa.
//
// Cuidado com o `limit: 0`: o filtro abaixo deixa passar o zero de propósito
// (só corta `undefined`, `null` e vazio), porque `?limit=0` é justamente o que
// diz ao servidor para não limitar.
export const getOrders = async (baseUrl, token, params = {}, { signal } = {}) => {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  return jsonFetch(`${baseUrl}/woocommerce/get/orders${query ? `?${query}` : ''}`, {
    method: 'GET',
    headers: authHeaders(token),
    signal,
  });
};

// Reembolsar uma encomenda, total ou parcialmente.
//
// `amount` em euros; sem ele devolve tudo o que falta. `method` é 'bank'
// (dinheiro de volta) ou 'voucher' (crédito na conta) — o que o cliente pediu
// vem no cartão, mas quem decide é quem está ao telefone com ele.
export const refundOrder = async (baseUrl, token, orderId, { amount, method, reason } = {}) => {
  return jsonFetch(`${baseUrl}/woocommerce/orders/${orderId}/refund`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ amount, method, reason }),
  });
};

// Fecha o pedido de reembolso que o cliente sinalizou na app, sem devolver nada.
// Para quando se falou com ele e não havia nada a devolver.
export const resolveRefundRequest = async (baseUrl, token, orderId, { status, note } = {}) => {
  return jsonFetch(`${baseUrl}/woocommerce/orders/${orderId}/refund-request`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ status, note }),
  });
};

export const updateOrderStatus = async (baseUrl, token, orderId, newStatus) => {
  return jsonFetch(`${baseUrl}/woocommerce/update/orders/${orderId}/status`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ new_status: newStatus }),
  });
};

export const sendDeliveryNotification = async (baseUrl, token, payload) => {
  return jsonFetch(`${baseUrl}/whatsapp/notification/order-on-delivery`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
};

export const sendCompletedNotification = async (baseUrl, token, payload) => {
  return jsonFetch(`${baseUrl}/whatsapp/notification/completed`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
};

// Reservations API
//
// `from` (YYYY-MM-DD) narrows the query in the database instead of downloading
// everything and filtering in the browser. Without it the backend returns every
// reservation ever made — 4031 rows and ~950 KB at the time of writing, growing
// forever, on every page load.
//
// There is deliberately no upper bound: future bookings must always come back.
// Omitting `from` keeps the previous behaviour, which is what the "Mostrar
// todas" toggle and the "Tudo" statistics period need.
export const getReservations = async (baseUrl, token, { from } = {}) => {
  const query = from ? `?from=${encodeURIComponent(from)}` : '';

  return jsonFetch(`${baseUrl}/wpcafe/reservations${query}`, {
    method: 'GET',
    headers: authHeaders(token),
  });
};

export const createReservation = async (baseUrl, token, reservationData) => {
  return jsonFetch(`${baseUrl}/wpcafe/reservations`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(reservationData),
  });
};

export const updateReservation = async (baseUrl, token, reservationId, reservationData) => {
  return jsonFetch(`${baseUrl}/wpcafe/reservations/${reservationId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(reservationData),
  });
};

export const sendReservationConfirmedNotification = async (baseUrl, token, payload) => {
  return jsonFetch(`${baseUrl}/whatsapp/notification/reservation_confirmed`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
};

// Restaurant Configuration API
export const getRestaurantConfigs = async (baseUrl, token) => {
  return jsonFetch(`${baseUrl}/restaurant-config`, {
    method: 'GET',
    headers: authHeaders(token),
  });
};

export const getRestaurantConfig = async (baseUrl, token, slug) => {
  return jsonFetch(`${baseUrl}/restaurant-config/${slug}`, {
    method: 'GET',
    headers: authHeaders(token),
  });
};

export const updateRestaurantConfig = async (baseUrl, token, slug, configData) => {
  return jsonFetch(`${baseUrl}/restaurant-config/${slug}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(configData),
  });
};
